import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Sparkline } from "../../components/Sparkline";
import { TrendingUp, TrendingDown, Users, Receipt, Package } from "lucide-react";
import { useSupplier } from "../../context/SupplierContext";
import {
  SCORE_SIGNAL_LABELS,
  SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT,
  SCORE_WEIGHTED_KEYS,
  SCORE_TIER_EXPLAINERS,
  SCORE_DISCLAIMER,
  normalizeRating,
} from "../../lib/scoreLabels";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../../components/ui/popover";
import { Info } from "lucide-react";

export default function BuyerScore() {
  const { active: seller } = useSupplier();
  const sellerName = seller?.tenant_name || null;
  const sellerId = seller?.tenant_owner_user_id || null;
  const [s, setS] = useState(null);
  const [hist, setHist] = useState(null);
  const [events, setEvents] = useState([]);
  const [peers, setPeers] = useState(null);
  const [whatif, setWhatif] = useState(0);
  const [trust, setTrust] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/score"),
      api.get("/scoring/history"),
      api.get("/scoring/events"),
      api.get("/scoring/peers"),
      sellerId ? api.get(`/buyer/trust/${sellerId}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]).then(([a, b, c, d, t]) => {
      setS(a.data); setHist(b.data); setEvents(c.data); setPeers(d.data);
      setTrust(t.data);
    });
  }, [sellerId]);

  const projected = useMemo(() => {
    if (!s) return 0;
    return Math.min(850, s.score + Math.round(whatif * 0.3));
  }, [s, whatif]);

  if (!s) return <div className="p-8 text-foreground/60">Loading…</div>;

  // v5.2 · use the LIVE weights returned by /api/score (single source of
  // truth — backend WEIGHTS dict). Fall back to v5.2 defaults if the
  // server is on an older deployment.
  const weights = (() => {
    if (s.weights && Object.keys(s.weights).length) {
      // Backend returns fractions (0.22) — convert to display %.
      const out = {};
      for (const [k, v] of Object.entries(s.weights)) out[k] = Math.round(v * 100);
      return out;
    }
    return SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT;
  })();
  const labels = SCORE_SIGNAL_LABELS;

  const pageTitle = sellerName ? `Your trust with ${sellerName}` : "My Verqo Trade Score";
  const pageSubtitle = sellerName
    ? `How ${sellerName} sees you · plus your platform-wide Verqo Trade Score`
    : "Wholesale trade intelligence — built on real transactions";

  // Tier label/color helpers for the per-seller card.
  const TIER_PILLS = {
    trusted_partner: { label: "Trusted partner", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    established:     { label: "Established",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
    watchlist:       { label: "On watchlist",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
    high_risk:       { label: "Limited history", cls: "bg-slate-100 text-slate-700 border-slate-300" },
    new_partner:     { label: "New partner",     cls: "bg-foreground/10 text-foreground/70 border-foreground/15" },
  };
  const tierPill = trust ? (TIER_PILLS[trust.tier] || TIER_PILLS.new_partner) : null;

  const ratingLabel = normalizeRating(s.rating);
  const ratingExplainer = s.tier_explainer || SCORE_TIER_EXPLAINERS[ratingLabel] || "";
  const disclaimer = s.disclaimer || SCORE_DISCLAIMER;

  return (
    <>
      <PageHeader title={pageTitle} subtitle={pageSubtitle}/>
      <div className="p-8 space-y-6">

        {/* v5.2 · Neutral disclaimer — verqo is a data provider, not a lender */}
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-[12.5px] leading-relaxed flex items-start gap-2.5"
          data-testid="score-disclaimer"
          role="note"
        >
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700"/>
          <div>
            <span className="font-semibold">Informational only.</span>{" "}
            {disclaimer}
            <a
              href="/buyer/settings#trade-score"
              className="inline-flex items-center gap-1 ml-1 underline underline-offset-2 hover:text-amber-900 whitespace-nowrap"
              data-testid="score-disclaimer-more-info"
            >
              Learn more →
            </a>
          </div>
        </div>

        {/* Per-seller trust strip — only when inside a seller's storefront. */}
        {sellerName && trust && (
          <section className="rounded-2xl border border-border bg-white p-6" data-testid="per-seller-trust">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Your relationship with</div>
                <h2 className="display text-xl mt-0.5">{sellerName}</h2>
              </div>
              {tierPill && (
                <div className={`chip border ${tierPill.cls}`} data-testid="trust-tier">{tierPill.label}</div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { lbl: "Orders placed",    val: trust.orders_placed,   icon: Package },
                { lbl: "Invoices paid",    val: trust.invoices_paid,   icon: Receipt },
                { lbl: "Currently overdue", val: trust.invoices_overdue, icon: TrendingDown,
                  tone: trust.invoices_overdue > 0 ? "rose" : null },
                { lbl: "On-time pay rate", val: trust.on_time_pay_rate == null ? "—" : `${trust.on_time_pay_rate}%`, icon: TrendingUp },
                { lbl: "Lifetime spend",   val: money(trust.lifetime_spend), icon: Receipt },
              ].map((row, i) => (
                <div key={i} className="rounded-xl bg-secondary/40 p-3" data-testid={`trust-stat-${i}`}>
                  <row.icon className={`w-3.5 h-3.5 ${row.tone === "rose" ? "text-rose-500" : "text-foreground/45"}`}/>
                  <div className="text-[10px] uppercase tracking-widest text-foreground/55 mt-1.5">{row.lbl}</div>
                  <div className={`mt-1 display text-lg ${row.tone === "rose" ? "text-rose-700" : ""}`}>{row.val}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between text-[11.5px] text-foreground/55">
              <span>
                Suggested terms with <strong>{sellerName}</strong>: <span className="mono uppercase tracking-wider text-foreground/80">{trust.suggested_terms?.replace("_", " ")}</span>
              </span>
              <span className="text-foreground/45">
                {trust.breakdown?.regular_orders + trust.breakdown?.dropship_orders} orders ·
                {" "}{trust.breakdown?.regular_invoices + trust.breakdown?.dropship_invoices} invoices analyzed
              </span>
            </div>
          </section>
        )}

        {/* Top: big card + history chart */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="card-ink rounded-2xl p-7 lg:col-span-1 relative overflow-hidden" data-testid="score-big">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent/15 blur-3xl"/>
            <div className="relative">
              <div className="text-xs mono uppercase tracking-widest text-accent/80">{sellerName ? "Your Verqo Trade Score" : "Current score"}</div>
              <div className="display text-7xl mt-2">{s.score}</div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="chip bg-accent/30 border border-accent/40 text-background inline-flex items-center gap-1 hover:bg-accent/40 transition-colors"
                      data-testid="score-tier-pill"
                      aria-label={`Tier ${ratingLabel} — explanation`}
                    >
                      {ratingLabel}
                      <Info className="w-3 h-3 opacity-70"/>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="bottom"
                    align="start"
                    className="max-w-xs text-[12.5px] leading-relaxed"
                    data-testid="score-tier-popover"
                  >
                    <div className="font-medium mb-1">{ratingLabel}</div>
                    <div className="text-foreground/70">{ratingExplainer}</div>
                    <div className="text-[11px] text-foreground/45 mt-2">
                      Description of observed data — not a credit recommendation.
                    </div>
                  </PopoverContent>
                </Popover>
                {s.percentile != null && (
                  <span
                    className="chip bg-background/10 border border-background/20 text-background/85"
                    data-testid="score-percentile"
                    title={`Percentile vs ${s.cohort_size || 0} peer buyers on verqo`}
                  >
                    Top {Math.max(1, 100 - s.percentile)}% of peers
                  </span>
                )}
                {hist?.delta_30d != null && (
                  <div className={`text-xs flex items-center gap-1 ${hist.delta_30d >= 0 ? "text-accent" : "text-rose-300"}`}>
                    {hist.delta_30d >= 0 ? <TrendingUp className="w-3 h-3"/> : <TrendingDown className="w-3 h-3"/>}
                    {hist.delta_30d >= 0 ? "+" : ""}{hist.delta_30d} this month
                  </div>
                )}
              </div>
              <div className="mt-7 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-background/60">Credit limit</span><span className="display">{money(s.credit_limit)}</span></div>
                <div className="flex justify-between"><span className="text-background/60">Rate</span><span className="mono">{(s.rate*100).toFixed(2)}%</span></div>
                <div className="flex justify-between"><span className="text-background/60">Volume analyzed</span><span className="mono">{money(s.total_volume)}</span></div>
                <div className="flex justify-between"><span className="text-background/60">Invoices</span><span className="mono">{s.total_invoices}</span></div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-2xl bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-lg">12-month trend</h2>
              <span className="text-xs text-foreground/60">range {hist?.points && Math.min(...hist.points.map(p=>p.score))} – {hist?.points && Math.max(...hist.points.map(p=>p.score))}</span>
            </div>
            <div className="h-40">
              {hist?.points && <Sparkline data={hist.points.map(p => ({ value: p.score }))} stroke="#0A1F1C" fill="rgba(232,255,58,0.25)" height={160}/>}
            </div>
            <div className="grid grid-cols-12 gap-1 mt-1 text-[10px] mono text-foreground/50 text-center">
              {hist?.points?.map((p,i) => <span key={i}>{p.month}</span>)}
            </div>

            {/* Peer benchmark */}
            {peers && (
              <div className="mt-6 rounded-xl bg-secondary/50 p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/60 mb-3"><Users className="w-3 h-3"/>Peer benchmark</div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><div className="text-xs text-foreground/60">You</div><div className="display text-2xl">{peers.you}</div></div>
                  <div><div className="text-xs text-foreground/60">Peer avg</div><div className="display text-2xl">{peers.peer_avg}</div></div>
                  <div><div className="text-xs text-foreground/60">Top peer</div><div className="display text-2xl">{peers.peer_top}</div></div>
                </div>
                <div className="text-xs text-foreground/70 mt-3">You rank in the <span className="font-semibold">top {100 - peers.rank_pct}%</span> of buyers on verqo.</div>
              </div>
            )}
          </div>
        </div>

        {/* Signals + events */}
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="display text-lg mb-4">Signal breakdown · 10 weighted buckets</h2>
            <div className="space-y-4">
              {SCORE_WEIGHTED_KEYS.filter(k => s.signals[k] !== undefined).map(k => {
                const v = s.signals[k];
                return (
                  <div key={k} data-testid={`sig-${k}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span>{labels[k]} <span className="text-foreground/40 text-xs">· {weights[k]}%</span></span>
                      <span className="mono">{v}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-foreground" style={{width: `${v}%`}}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* What-if simulator */}
            <div className="mt-7 rounded-xl bg-foreground text-background p-5">
              <div className="text-xs mono uppercase tracking-widest text-accent/80">What-if simulator</div>
              <div className="mt-2 text-sm text-background/80">If I improve payment timeliness by:</div>
              <input type="range" min={0} max={30} step={1} value={whatif} onChange={e=>setWhatif(Number(e.target.value))} className="w-full mt-3" data-testid="whatif"/>
              <div className="flex justify-between text-xs mt-1 text-background/60"><span>0%</span><span className="mono">+{whatif}%</span><span>+30%</span></div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="text-xs text-background/60">Projected score in 60 days:</span>
                <span className="display text-3xl">{projected}</span>
                <span className="chip bg-accent/30 border border-accent/40">+{projected - s.score}</span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-border p-6">
            <h2 className="display text-lg mb-4">Recent score events</h2>
            <div className="space-y-2">
              {events.map((e,i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div className="flex-1">
                    <div className="text-sm">{e.label}</div>
                    <div className="text-xs text-foreground/50">{new Date(e.date).toLocaleDateString()}</div>
                  </div>
                  <span className={`mono text-sm ${e.type==="positive"?"text-emerald-600":e.type==="negative"?"text-rose-600":"text-foreground/40"}`}>{e.delta}</span>
                </div>
              ))}
              {events.length === 0 && <div className="text-center py-8 text-foreground/60 text-sm">No events yet — start trading to build score</div>}
            </div>

            <div className="mt-6 p-4 rounded-xl bg-secondary/40">
              <div className="text-xs mono uppercase tracking-widest text-foreground/60">How to improve</div>
              <ul className="mt-2 text-sm space-y-1.5 text-foreground/80">
                <li>• Pay net-terms invoices on or before due date (22% weight)</li>
                <li>• Avoid 90+ day overdue / disputes (15%)</li>
                <li>• Connect Plaid to verify cashflow (12%)</li>
                <li>• Keep credit utilization below 30% (10%)</li>
                <li>• Maintain a wallet balance for liquidity (10%)</li>
                <li>• Keep your public record clean — no UCC liens, no judgments (10%)</li>
                <li>• Diversify across 2-3 suppliers, avoid sudden order spikes (5%)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Engine transparency — v5.2: 10 weighted buckets + Stage-1 gate */}
        {s.breakdown && (
          <div className="rounded-2xl bg-white border border-border p-6" data-testid="score-engine-breakdown">
            <div className="flex items-center justify-between mb-4">
              <h2 className="display text-lg">Score engine — what we counted</h2>
              <span className="text-xs text-foreground/55">Live · v5.2 · 10 weighted buckets + Stage-1 gate</span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {/* Net-terms payment */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-net-terms">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><Receipt className="w-3.5 h-3.5"/>Net-terms payments</div>
                <div className="display text-2xl mt-1.5">{s.breakdown.net_terms_payment?.total_net_terms ?? 0}</div>
                <div className="text-[11px] text-foreground/55 mt-1">credit invoices analysed</div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">On time</span><span className="mono text-emerald-700">{s.breakdown.net_terms_payment?.paid_on_time ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">1-30d late</span><span className="mono text-amber-700">{s.breakdown.net_terms_payment?.paid_late_1_30 ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">31-60d late</span><span className="mono text-amber-700">{s.breakdown.net_terms_payment?.paid_late_31_60 ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">60+ late</span><span className="mono text-rose-700">{s.breakdown.net_terms_payment?.paid_late_60plus ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Currently overdue</span><span className="mono text-rose-700">{s.breakdown.net_terms_payment?.currently_overdue ?? 0}</span></div>
                </div>
              </div>

              {/* Credit utilization */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-credit-util">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><TrendingUp className="w-3.5 h-3.5"/>Credit utilization</div>
                <div className="display text-2xl mt-1.5">
                  {(s.breakdown.credit_utilization?.utilization_pct ?? 0)}%
                </div>
                <div className="text-[11px] text-foreground/55 mt-1">
                  {s.breakdown.credit_utilization?.utilization_band || "no_data"}
                </div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">Approved limit</span><span className="mono">{money(s.breakdown.credit_utilization?.approved_limit ?? 0)}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Outstanding</span><span className="mono">{money(s.breakdown.credit_utilization?.outstanding_on_credit ?? 0)}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Cap collisions (90d)</span><span className="mono">{s.breakdown.credit_utilization?.cap_collisions_90d ?? 0}</span></div>
                </div>
              </div>

              {/* Default & dispute events */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-defaults">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><TrendingDown className="w-3.5 h-3.5"/>Default events</div>
                <div className="display text-2xl mt-1.5 text-rose-700">{(s.breakdown.default_events?.defaults_90d_plus_24mo ?? 0)
                  + (s.breakdown.default_events?.adverse_actions_6mo ?? 0)
                  + (s.breakdown.default_events?.open_claims_over_30d ?? 0)
                  + (s.breakdown.default_events?.fraud_rejected_returns ?? 0)}</div>
                <div className="text-[11px] text-foreground/55 mt-1">total flags in scoring window</div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">Defaults 90+ days (24mo)</span><span className="mono text-rose-700">{s.breakdown.default_events?.defaults_90d_plus_24mo ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Adverse actions (6mo)</span><span className="mono">{s.breakdown.default_events?.adverse_actions_6mo ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Open claims &gt;30d</span><span className="mono">{s.breakdown.default_events?.open_claims_over_30d ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Fraud-rejected returns</span><span className="mono">{s.breakdown.default_events?.fraud_rejected_returns ?? 0}</span></div>
                </div>
              </div>

              {/* Cashflow */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-cashflow">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><TrendingUp className="w-3.5 h-3.5"/>Cashflow (Plaid)</div>
                <div className="display text-2xl mt-1.5">{s.breakdown.cashflow?.months_of_data ?? 0}<span className="text-sm text-foreground/55 ml-1">mo</span></div>
                <div className="text-[11px] text-foreground/55 mt-1">
                  {s.breakdown.cashflow?.plaid_connected ? "bank connected" : "connect Plaid to unlock"}
                </div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">NSF events</span><span className="mono">{s.breakdown.cashflow?.nsf_count ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Avg monthly revenue</span><span className="mono">{money(s.breakdown.cashflow?.avg_monthly_revenue ?? 0)}</span></div>
                </div>
              </div>

              {/* KYB verification · Stage-1 gate (display-only · not weighted) */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-kyb">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55">
                  <Users className="w-3.5 h-3.5"/>KYB · Stage-1 gate
                </div>
                <div className="display text-2xl mt-1.5">
                  {s.breakdown.kyb?.kyb_status === "approved" ? "✓" : "—"}
                </div>
                <div className="text-[11px] text-foreground/55 mt-1">KYB status</div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">TIN match</span><span className="mono">{s.breakdown.kyb?.tin_match ? "✓" : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">SOS active</span><span className="mono">{s.breakdown.kyb?.sos_active ? "✓" : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">No public liens</span><span className="mono">{s.breakdown.kyb?.liens ? "—" : "✓"}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Officers verified</span><span className="mono">{s.breakdown.kyb?.officers_verified ? "✓" : "—"}</span></div>
                </div>
              </div>

              {/* Credit history */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-credit-history">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><Receipt className="w-3.5 h-3.5"/>Credit-line history</div>
                <div className="display text-2xl mt-1.5">{s.breakdown.credit_history?.applications_total ?? 0}</div>
                <div className="text-[11px] text-foreground/55 mt-1">total applications</div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">Approved</span><span className="mono text-emerald-700">{s.breakdown.credit_history?.approved ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Denied</span><span className="mono text-amber-700">{s.breakdown.credit_history?.denied ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Seller credit offers</span><span className="mono text-emerald-700">{s.breakdown.credit_history?.seller_credit_offers ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Revocations</span><span className="mono text-rose-700">{s.breakdown.credit_history?.revocations ?? 0}</span></div>
                </div>
              </div>

              {/* v5.2 · Public records overlay */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-public-records">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><Receipt className="w-3.5 h-3.5"/>Public records</div>
                <div className="display text-2xl mt-1.5">
                  {((s.breakdown.public_records?.ucc_filings_active ?? 0)
                    + (s.breakdown.public_records?.tax_liens_active ?? 0)
                    + (s.breakdown.public_records?.civil_judgments_active ?? 0)) === 0 ? "✓" : "—"}
                </div>
                <div className="text-[11px] text-foreground/55 mt-1">
                  {s.breakdown.public_records?.is_mock_clean ? "no records on file (preview)" : "active records"}
                </div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">UCC-1 blanket liens</span><span className="mono">{s.breakdown.public_records?.ucc_filings_active ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Tax liens (IRS/state)</span><span className="mono">{s.breakdown.public_records?.tax_liens_active ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Civil judgments</span><span className="mono">{s.breakdown.public_records?.civil_judgments_active ?? 0}</span></div>
                </div>
              </div>

              {/* v5.2 · Concentration & velocity (includes Platform GMV) */}
              <div className="rounded-xl bg-secondary/40 p-4" data-testid="bd-velocity">
                <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-foreground/55"><TrendingUp className="w-3.5 h-3.5"/>Concentration &amp; velocity</div>
                <div className="display text-2xl mt-1.5">{s.breakdown.concentration_velocity?.orders_per_month_90d ?? 0}</div>
                <div className="text-[11px] text-foreground/55 mt-1">orders / month (90d)</div>
                <div className="mt-3 space-y-1 text-[12px]">
                  <div className="flex justify-between"><span className="text-foreground/65">Spend / month</span><span className="mono">{money(s.breakdown.concentration_velocity?.spend_per_month_90d ?? 0)}</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Top supplier concentration</span><span className="mono">{(s.breakdown.concentration_velocity?.top_seller_concentration_pct ?? 0).toFixed(0)}%</span></div>
                  <div className="flex justify-between"><span className="text-foreground/65">Lifetime GMV on verqo</span><span className="mono">{money(s.breakdown.concentration_velocity?.lifetime_gmv_usd ?? 0)}</span></div>
                </div>
              </div>
            </div>

            {/* v5.2 · Stage-1 Eligibility Gate (separate from scored buckets) */}
            {s.eligibility_gate && (
              <div className="mt-5 rounded-xl border border-border bg-white p-4" data-testid="eligibility-gate-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs mono uppercase tracking-widest text-foreground/55">Stage-1 Eligibility · {s.eligibility_gate.eligible ? "Passing" : "Action required"}</div>
                  <span className={`text-[10px] mono px-2 py-0.5 rounded-full ${s.eligibility_gate.eligible ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                    {s.eligibility_gate.eligible ? "ELIGIBLE" : "GATE FAIL"}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-2 text-[12.5px]">
                  {(s.eligibility_gate.gates || []).map(g => (
                    <div key={g.key} className="flex items-start gap-2">
                      <span className={`mono ${g.ok ? "text-emerald-700" : (g.kind === "hard" ? "text-rose-700" : "text-amber-700")}`}>
                        {g.ok ? "✓" : "—"}
                      </span>
                      <div>
                        <div className="font-medium">{g.key.replace(/_/g, " ")}</div>
                        {!g.ok && g.detail && <div className="text-[11px] text-foreground/55">{g.detail}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {s.eligibility_gate.blocking_reasons?.length > 0 && (
                  <div className="mt-3 text-[11px] text-rose-700">
                    Score won't be approvable until: {s.eligibility_gate.blocking_reasons.join(" · ")}
                  </div>
                )}
              </div>
            )}

            {/* Caps · bonuses · guardrails applied */}
            {(s.caps_applied?.length || s.bonuses_applied?.length || s.guardrails_applied?.length) ? (
              <div className="mt-5 grid md:grid-cols-3 gap-4" data-testid="caps-bonuses">
                {s.caps_applied?.length > 0 && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                    <div className="text-xs mono uppercase tracking-widest text-rose-700 flex items-center gap-2"><TrendingDown className="w-3.5 h-3.5"/>Hard caps</div>
                    <ul className="mt-2 text-[12px] text-rose-800 space-y-1">
                      {s.caps_applied.map((c, i) => <li key={i}>• {c}</li>)}
                    </ul>
                  </div>
                )}
                {s.bonuses_applied?.length > 0 && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="text-xs mono uppercase tracking-widest text-emerald-700 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5"/>Bonuses</div>
                    <ul className="mt-2 text-[12px] text-emerald-800 space-y-1">
                      {s.bonuses_applied.map((b, i) => <li key={i}>• {b}</li>)}
                    </ul>
                  </div>
                )}
                {s.guardrails_applied?.length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="guardrails-panel">
                    <div className="text-xs mono uppercase tracking-widest text-amber-700 flex items-center gap-2"><TrendingDown className="w-3.5 h-3.5"/>Guardrails</div>
                    <ul className="mt-2 text-[12px] text-amber-800 space-y-1">
                      {s.guardrails_applied.map((g, i) => <li key={i}>• {g}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
